import { create } from '@bufbuild/protobuf';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import { Client } from '@connectrpc/connect';

import { Clearable, clearableToWire } from './clearable';
import {
  OrganizationService,
  UpdateOrganizationRequest_StringArraySchema,
  SubscriptionStatus as PbSubscriptionStatus,
} from './espb/organization_service_pb';
import { knownEnumOrDefault, requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto, toSyncUpdateProfile } from './sync';
import {
  OrganizationId,
  SyncUpdate,
  UserId,
  VaultCreationDto,
  NewLockDto,
  VaultPairCreationDto,
  ProfileDto,
  Email,
  ProfileId,
  SubscriptionDetailsDto,
  CreateProfileOpDto,
  RegeneratedProfileDto,
  BreachData,
  toPbProfileRegenerateData,
  UpdateProfileOpDto,
  SubscriptionPlan,
  SubscriptionStatus,
  ProfileProfileLockDto,
  UpstreamProfileProfileLockDto,
  VaultId,
  GenerationId,
  VaultProfileLockDto,
  toVaultProfileLockDto,
  SyncUpdateProfile,
  RolePermissionsDto,
  RoleId,
  ExportPermission,
} from './types';
import { toPbVaultCreationData, toVaultPairCreationData } from './vaults';

export default class OrganizationServiceClient {
  constructor(private readonly espbClient: Client<typeof OrganizationService>) {}

  async create({
    name,
    email,
    domain,
    icon,
    userProfile,
    adminProfile,
    personalVaultDto,
    personalVaultSuperuserLock,
    teamVaultDto,
    teamVaultSuperuserLock,
    teamAssociatedVaultSuperuserLock,
    contactJson,
    parentOrganizationInfo,
    adminVaultDto,
    loginSummaryVaultDto,
  }: {
    name: string;
    domain?: string;
    email: Email;
    icon?: string;
    userProfile: ProfileDto | undefined;
    adminProfile?: ProfileDto;
    personalVaultDto?: VaultCreationDto;
    personalVaultSuperuserLock?: NewLockDto;
    teamVaultDto?: VaultPairCreationDto;
    teamVaultSuperuserLock?: NewLockDto;
    teamAssociatedVaultSuperuserLock?: NewLockDto;
    contactJson?: string;
    parentOrganizationInfo?: {
      id: OrganizationId;
      downstreamAdminProfileLock: UpstreamProfileProfileLockDto;
    };
    adminVaultDto?: VaultCreationDto;
    loginSummaryVaultDto?: VaultCreationDto;
  }): Promise<{
    organizationId: OrganizationId;
    profileId: ProfileId;
    syncUpdate: SyncUpdate;
  }> {
    const resp = await this.espbClient.create({
      name,
      email,
      domain,
      icon,
      userProfile,
      adminProfile,
      adminVaultData: adminVaultDto ? toPbVaultCreationData(adminVaultDto) : undefined,
      loginSummaryVaultData:
        loginSummaryVaultDto ? toPbVaultCreationData(loginSummaryVaultDto) : undefined,
      personalVaultData: personalVaultDto ? toPbVaultCreationData(personalVaultDto) : undefined,
      personalVaultSuperuserEncryptedStorableVaultKey:
        personalVaultSuperuserLock?.encryptedStorableVaultKey,
      personalVaultSuperuserEncryptedHighSecurityVaultKey:
        personalVaultSuperuserLock?.encryptedHighSecurityVaultKey,
      personalVaultSuperuserEncryptedVaultMessagePrivateKey:
        personalVaultSuperuserLock?.encryptedVaultMessagePrivateKey,
      teamVaultData: teamVaultDto ? toVaultPairCreationData(teamVaultDto) : undefined,
      teamVaultSuperuserEncryptedStorableVaultKey:
        teamVaultSuperuserLock?.encryptedStorableVaultKey,
      teamVaultSuperuserEncryptedHighSecurityVaultKey:
        teamVaultSuperuserLock?.encryptedHighSecurityVaultKey,
      teamVaultSuperuserEncryptedVaultMessagePrivateKey:
        teamVaultSuperuserLock?.encryptedVaultMessagePrivateKey,
      teamAssociatedVaultSuperuserEncryptedStorableVaultKey:
        teamAssociatedVaultSuperuserLock?.encryptedStorableVaultKey,
      teamAssociatedVaultSuperuserEncryptedHighSecurityVaultKey:
        teamAssociatedVaultSuperuserLock?.encryptedHighSecurityVaultKey,
      teamAssociatedVaultSuperuserEncryptedVaultMessagePrivateKey:
        teamAssociatedVaultSuperuserLock?.encryptedVaultMessagePrivateKey,
      contactJson,
      parentOrganizationInfo,
    });
    return {
      organizationId: requireFieldNotEmpty(resp.organizationId),
      profileId: requireFieldNotEmpty(resp.profileId),
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
    };
  }

  async update(
    organizationId: OrganizationId,
    {
      name,
      icon,
      restrictCreateTeam,
      restrictPersonalLogins,
      auditlogEnable,
      childOrganizationManagementEnable,
      entraPreferUserPrincipalName,
      monitoredDomains,
      passwordPolicy,
      unlockTimeLimit,
      clientSettings,
      roleManagementEnable,
      userRoleExportPermission,
    }: {
      name?: string;
      icon?: Clearable<string>;
      restrictCreateTeam?: boolean;
      restrictPersonalLogins?: boolean;
      auditlogEnable?: boolean;
      childOrganizationManagementEnable?: boolean;
      roleManagementEnable?: boolean;
      entraPreferUserPrincipalName?: boolean;
      monitoredDomains?: string[];
      passwordPolicy?: string;
      unlockTimeLimit?: {
        enabled: boolean;
        minutes: number;
      };
      clientSettings?: string;
      userRoleExportPermission?: ExportPermission;
    },
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.update({
      organizationId,
      name,
      icon: clearableToWire(icon),
      restrictCreateTeam,
      restrictPersonalLogins,
      auditlogEnable,
      childOrganizationManagementEnable,
      roleManagementEnable,
      entraPreferUserPrincipalName,
      monitoredDomains:
        monitoredDomains ?
          create(UpdateOrganizationRequest_StringArraySchema, { value: monitoredDomains })
        : undefined,
      passwordPolicy,
      unlockTimeLimit,
      clientSettings,
      userRoleExportPermission:
        userRoleExportPermission !== undefined ? { value: userRoleExportPermission } : undefined,
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async modifyMembers(
    orgId: OrganizationId,
    createProfiles: CreateProfileOpDto[],
    updateProfiles: UpdateProfileOpDto[],
    deleteProfileIds: ProfileId[],
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.modifyMembers({
      organizationId: orgId,
      createProfileOps: createProfiles.map((p) => ({
        userId: p.userId,
        email: p.email,
        roleId: p.roleId,
        profile: p.profileDto,
        adminProfileLock: p.adminProfileLock,
        personalVaultData: toPbVaultCreationData(p.personalVaultDto),
        createStartCodeAndSendMail: p.createStartCodeAndSendMail,
        setStartCodeEmailSender: p.setStartCodeEmailSender,
        entraUserId: p.entraUserId,
      })),
      updateProfileOps: updateProfiles.map((p) => ({
        profileId: p.profileId,
        roleId: p.roleId,
        adminProfileLock: p.adminProfileLock,
        childOrganizationManagementShow: p.childOrganizationManagementShow,
      })),
      deleteProfileIds,
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async createServiceProfile(
    orgId: OrganizationId,
    profile: ProfileDto,
    downstreamAdminProfileLock: ProfileProfileLockDto,
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.createServiceProfile({
      organizationId: orgId,
      profile,
      downstreamAdminProfileLock,
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async acceptInvitation(organizationId: OrganizationId): Promise<SyncUpdate> {
    const resp = await this.espbClient.acceptInvite({ organizationId });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async redeemCoupon(organizationId: OrganizationId, couponText: string): Promise<SyncUpdate> {
    const resp = await this.espbClient.redeemCoupon({ organizationId, couponText });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async leave(organizationId: OrganizationId): Promise<SyncUpdate> {
    const resp = await this.espbClient.leave({ organizationId });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async getSubscriptionDetails(organizationId: OrganizationId): Promise<SubscriptionDetailsDto> {
    const resp = await this.espbClient.getSubscriptionDetails({ organizationId });

    const payments = resp.payments.map((p) => ({
      eventTime: timestampDate(requireFieldNotEmpty(p.eventTime)),
      receiptUrl: requireFieldNotEmpty(p.receiptUrl),
      saleGrossCents: Number(p.saleGrossCents),
      paymentTaxCents: Number(p.paymentTaxCents),
      currency: requireFieldNotEmpty(p.currency),
      orderId: requireFieldNotEmpty(p.orderId),
    }));

    return {
      payments,
      paddleSubscription:
        resp.paddleSubscription ?
          {
            ...resp.paddleSubscription,
            subscriptionPlan: knownEnumOrDefault(resp.paddleSubscription.plan, SubscriptionPlan),
            nextBillDate:
              resp.paddleSubscription.nextBillDate ?
                timestampDate(resp.paddleSubscription.nextBillDate)
              : undefined,
          }
        : undefined,
      status: toSubscriptionStatus(resp.status),
      billableUsers: resp.billableUsers,
    };
  }

  async requestAdditionalLicenses(
    organizationId: OrganizationId,
    additionalLicenseCount: number,
    message: string,
  ): Promise<void> {
    await this.espbClient.requestAdditionalLicenses({
      organizationId,
      additionalLicenseCount,
      message,
    });
  }

  async regenerateOrganizationProfile(
    organizationId: OrganizationId,
    userId: UserId | undefined,
    p: RegeneratedProfileDto,
    childOrganizationProfiles?: RegeneratedProfileDto[],
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.regenerateOrganizationProfile({
      organizationId,
      userId,
      profile: toPbProfileRegenerateData(p),
      childOrganizationProfiles: childOrganizationProfiles?.map(toPbProfileRegenerateData),
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async regenerateAdminProfile(
    orgId: OrganizationId,
    p: RegeneratedProfileDto,
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.regenerateAdminProfile({
      organizationId: orgId,
      profile: toPbProfileRegenerateData(p),
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async delete(organizationId: OrganizationId): Promise<SyncUpdate> {
    const resp = await this.espbClient.delete({ organizationId });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async renewProfileStartCode(
    organizationId: OrganizationId,
    ...profileIds: ProfileId[]
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.renewProfileStartCode({ organizationId, profileIds });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async sendStartCodeEmail(
    organizationId: OrganizationId,
    ...profileIds: ProfileId[]
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.sendStartCodeEmail({ organizationId, profileIds });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async revokeProfileStartCode(
    organizationId: OrganizationId,
    ...profileIds: ProfileId[]
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.revokeProfileStartCode({ organizationId, profileIds });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async getDeletionInfo(
    organizationId: OrganizationId,
    token: string,
  ): Promise<{
    organizationName: string;
    requestedAt: Date;
    requestedByEmail: Email;
    userCount: number;
  }> {
    const resp = await this.espbClient.getDeletionInfo({ organizationId, token });
    return {
      organizationName: resp.organizationName,
      requestedAt: timestampDate(requireFieldNotEmpty(resp.requestedAt)),
      requestedByEmail: resp.requestedByEmail,
      userCount: resp.userCount,
    };
  }

  async deleteWithToken(organizationId: OrganizationId, token: string): Promise<void> {
    await this.espbClient.deleteWithToken({ organizationId, token });
  }

  async removeDeletionToken(organizationId: OrganizationId, token: string): Promise<void> {
    await this.espbClient.removeDeletionToken({ organizationId, token });
  }

  async getBreachData(organizationId: OrganizationId): Promise<BreachData> {
    const resp = await this.espbClient.getBreachData({ organizationId });

    return {
      ...resp,
      lastCheckDate: timestampDate(requireFieldNotEmpty(resp.lastCheckDate)),
      domains: resp.domains.map((domain) => ({
        ...domain,
        breaches: domain.breaches.map((breach) => {
          const model = requireFieldNotEmpty(breach.model);
          return {
            ...breach,
            model: {
              ...model,
              breachDate: timestampDate(requireFieldNotEmpty(model.breachDate)),
              addedDate: timestampDate(requireFieldNotEmpty(model.addedDate)),
            },
          };
        }),
      })),
    };
  }

  async listLocksForProfileRegenerate(
    organizationId: OrganizationId,
    profileId: ProfileId,
  ): Promise<{
    vaults: {
      id: VaultId;
      generationId: GenerationId;
      adminVaultProfileLockDto: VaultProfileLockDto;
    }[];
    childOrganizations: {
      id: OrganizationId;
      profileId: ProfileId;
      adminProfile: SyncUpdateProfile;
      vaults: {
        id: VaultId;
        generationId: GenerationId;
        adminVaultProfileLockDto: VaultProfileLockDto;
      }[];
    }[];
  }> {
    const resp = await this.espbClient.listLocksForProfileRegenerate({
      organizationId,
      profileId,
    });
    return {
      vaults: resp.vaults.map((v) => ({
        id: requireFieldNotEmpty(v.id),
        generationId: requireFieldNotEmpty(v.generationId),
        adminVaultProfileLockDto: requireFieldNotEmpty(
          toVaultProfileLockDto(v.adminVaultProfileLock),
        ),
      })),
      childOrganizations: resp.childOrganizations.map((co) => ({
        id: requireFieldNotEmpty(co.id),
        profileId: requireFieldNotEmpty(co.profileId),
        adminProfile: toSyncUpdateProfile(requireFieldNotEmpty(co.adminProfile)),
        vaults: co.vaults.map((v) => ({
          id: requireFieldNotEmpty(v.id),
          generationId: requireFieldNotEmpty(v.generationId),
          adminVaultProfileLockDto: requireFieldNotEmpty(
            toVaultProfileLockDto(v.adminVaultProfileLock),
          ),
        })),
      })),
    };
  }

  async createLoginSummaryVault(
    organizationId: OrganizationId,
    loginSummaryVaultData: VaultCreationDto,
  ): Promise<{ syncUpdate: SyncUpdate; loginSummaryVaultId: VaultId }> {
    const resp = await this.espbClient.createLoginSummaryVault({
      organizationId,
      loginSummaryVaultData: toPbVaultCreationData(loginSummaryVaultData),
    });
    return {
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
      loginSummaryVaultId: requireFieldNotEmpty(resp.loginSummaryVaultId),
    };
  }

  async addRole(
    organizationId: OrganizationId,
    role: { customName: string; permissions: RolePermissionsDto },
  ): Promise<{ syncUpdate: SyncUpdate; roleId: RoleId }> {
    const resp = await this.espbClient.addRole({
      organizationId,
      customName: role.customName,
      permissions: role.permissions,
    });
    return {
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
      roleId: requireFieldNotEmpty(resp.roleId),
    };
  }

  async updateRole(
    organizationId: OrganizationId,
    roleId: RoleId,
    settings: {
      customName?: string;
      permissions?: RolePermissionsDto;
    },
    newAdminProfileProfileLocks: ProfileProfileLockDto[],
  ): Promise<SyncUpdate> {
    const resp = await this.espbClient.updateRole({
      organizationId,
      newAdminProfileProfileLocks,
      roleId,
      permissions: settings.permissions,
      customName: settings.customName,
    });

    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async deleteRole(organizationId: OrganizationId, roleId: RoleId): Promise<SyncUpdate> {
    const resp = await this.espbClient.deleteRole({
      organizationId,
      roleId,
    });

    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }
}

function toSubscriptionStatus(
  ps: PbSubscriptionStatus | undefined,
): SubscriptionStatus | undefined {
  if (!ps) {
    return undefined;
  }
  switch (ps.status.case) {
    case 'statusTrial': {
      const trial = requireFieldNotEmpty(ps.status.value);
      return {
        status: 'trial',
        trialEndDate: timestampDate(requireFieldNotEmpty(trial.trialEndDate)),
      };
    }
    case 'statusPaddleActive': {
      const active = requireFieldNotEmpty(ps.status.value);
      return {
        status: 'paddleActive',
        freeMembers: active.freeMembers,
        nextBillDate: timestampDate(requireFieldNotEmpty(active.nextBillDate)),
        baseUrl: requireFieldNotEmpty(active.baseUrl),
        email: requireFieldNotEmpty(active.email),
      };
    }
    case 'statusPaddlePastDue': {
      const pastDue = requireFieldNotEmpty(ps.status.value);
      return {
        status: 'paddlePastDue',
        freeMembers: pastDue.freeMembers,
        nextBillDate: timestampDate(requireFieldNotEmpty(pastDue.nextBillDate)),
        baseUrl: requireFieldNotEmpty(pastDue.baseUrl),
      };
    }
    case 'statusPaddlePausedVoluntary': {
      const pausedVoluntary = requireFieldNotEmpty(ps.status.value);
      return {
        status: 'paddlePausedVoluntary',
        freeMembers: pausedVoluntary.freeMembers,
        baseUrl: requireFieldNotEmpty(pausedVoluntary.baseUrl),
        pausedAt: timestampDate(requireFieldNotEmpty(pausedVoluntary.pausedAt)),
        pausedFrom: timestampDate(requireFieldNotEmpty(pausedVoluntary.pausedFrom)),
      };
    }
    case 'statusPaddlePausedDelinquent': {
      const pausedDelinquent = requireFieldNotEmpty(ps.status.value);
      return {
        status: 'paddlePausedDelinquent',
        freeMembers: pausedDelinquent.freeMembers,
        baseUrl: requireFieldNotEmpty(pausedDelinquent.baseUrl),
        pausedAt: timestampDate(requireFieldNotEmpty(pausedDelinquent.pausedAt)),
        pausedFrom: timestampDate(requireFieldNotEmpty(pausedDelinquent.pausedFrom)),
      };
    }
    case 'statusPaddleDeleted': {
      const deleted = requireFieldNotEmpty(ps.status.value);
      return {
        status: 'paddleDeleted',
        freeMembers: deleted.freeMembers,
        cancellationEffectiveDate: timestampDate(
          requireFieldNotEmpty(deleted.cancellationEffectiveDate),
        ),
      };
    }
    case 'statusActiveFree': {
      const activeFree = requireFieldNotEmpty(ps.status.value);
      return {
        status: 'activeFree',
        freeMembers: activeFree.freeMembers,
      };
    }
    case 'statusCustom': {
      const custom = requireFieldNotEmpty(ps.status.value);
      return {
        status: 'custom',
        left: custom.left,
        right: custom.right,
      };
    }
    case 'statusExample': {
      const example = requireFieldNotEmpty(ps.status.value);
      return {
        status: 'example',
        left: example.left,
        right: example.right,
      };
    }
  }
  return undefined;
}
